/**
 * SF-4651 step 2: the area to cover. The centre comes from a town or postcode (sf-api
 * POST /location/geocodeArea, SF-4643) or a point clicked on the map; then a radius and how many
 * locations to look for. The map shows the circle.
 */
import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { Button, Col, Form, Input, InputNumber, Row, Space, Typography } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { AreaMap } from './AreaMap';
import {
  demoErrorMessage,
  geocodeAreaArgs,
  useDemoMutation,
  type GeocodedArea,
} from '../api/demoBuildsClient';
import { MAX_RADIUS_MILES, MAX_SITES } from '../constants';
import type { AreaAnswer } from '../validation';

export interface AreaStepProps {
  value: AreaAnswer;
  error: string | null;
  /** A state setter: replies that arrive later are merged into the area as it is then. */
  onChange: Dispatch<SetStateAction<AreaAnswer>>;
}

const pointLabel = (lat: number, lng: number) => `Map point ${lat.toFixed(4)}, ${lng.toFixed(4)}`;

export const AreaStep: React.FC<AreaStepProps> = ({ value, error, onChange }) => {
  const [search, setSearch] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);
  const { mutateAsync, isLoading: finding } = useDemoMutation<GeocodedArea>();
  // Each search and map pick takes a number; a lookup answer is used only if nothing newer came.
  const latestChoice = useRef(0);

  const find = async () => {
    const text = search.trim();
    if (!text) {
      setSearchError('Enter a town or postcode');
      return;
    }
    setSearchError(null);
    const choice = ++latestChoice.current;
    try {
      const { data } = await mutateAsync(geocodeAreaArgs(text));
      if (choice !== latestChoice.current) return;
      if (typeof data?.lat !== 'number' || typeof data?.lng !== 'number') {
        setSearchError('No place found for that search');
        return;
      }
      onChange((current) => ({
        ...current,
        label: data.label || text,
        lat: data.lat,
        lng: data.lng,
      }));
    } catch (err) {
      if (choice !== latestChoice.current) return;
      setSearchError(demoErrorMessage(err, 'Could not look that place up. Try again.'));
    }
  };

  const centre =
    value.lat !== null && value.lng !== null ? { lat: value.lat, lng: value.lng } : null;

  return (
    <Form layout="vertical">
      <Form.Item
        label="Town or postcode"
        validateStatus={searchError ? 'error' : undefined}
        help={searchError ?? 'Or click a point on the map'}
        htmlFor="demo-area-search"
      >
        <Space.Compact style={{ width: '100%' }}>
          <Input
            id="demo-area-search"
            value={search}
            placeholder="Leeds, or LS1 4AP"
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={find}
          />
          <Button onClick={find} loading={finding} icon={<EnvironmentOutlined />}>
            Find
          </Button>
        </Space.Compact>
      </Form.Item>
      <Typography.Paragraph data-testid="area-centre">
        {centre ? (
          <>
            Centre: <strong>{value.label || pointLabel(centre.lat, centre.lng)}</strong>
          </>
        ) : (
          <Typography.Text type="secondary">No centre chosen yet</Typography.Text>
        )}
      </Typography.Paragraph>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            label="Radius (miles)"
            htmlFor="demo-radius"
            help={`Up to ${MAX_RADIUS_MILES} miles`}
          >
            <InputNumber
              id="demo-radius"
              min={0}
              max={MAX_RADIUS_MILES}
              step={1}
              value={value.radius_miles}
              onChange={(v) => onChange((current) => ({ ...current, radius_miles: v ?? null }))}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="Number of locations" htmlFor="demo-sites" help={`1 to ${MAX_SITES}`}>
            <InputNumber
              id="demo-sites"
              min={1}
              max={MAX_SITES}
              step={1}
              precision={0}
              value={value.max_sites}
              onChange={(v) => onChange((current) => ({ ...current, max_sites: v ?? null }))}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>
      {error && (
        <Typography.Paragraph type="danger" role="alert">
          {error}
        </Typography.Paragraph>
      )}
      <AreaMap
        centre={centre}
        radiusMiles={value.radius_miles}
        onPick={({ lat, lng }) => {
          latestChoice.current += 1;
          setSearchError(null);
          onChange((current) => ({ ...current, label: pointLabel(lat, lng), lat, lng }));
        }}
      />
    </Form>
  );
};
